import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { PlanbookShare } from "@shared/models/analytics/planbook"
import type { User } from "@shared/models/user"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { assign, partial } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"
import createNarrative from "@shared/actions/create-narrative"
import defaultPlanbookContext from "../planbook/default-planbook-context"
import defaultPlanbookModels from "../planbook/default-planbook-models"

interface Payload {
  user: User
  planbookId: string
  planbookTitle: string
  perspective: string
  email: Array<string>
  subject: string
  message: string
  url: string
}

export default function emailPlanbookLinkAction(payload: Payload): PreparedAction {
  return {
    name: "EMAIL_PLANBOOK_LINK",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("user", payload.user.id).set("attributes.emailShareSettings", {
    email: payload.email,
    subject: payload.subject,
    message: payload.message,
  }).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  let models = defaultPlanbookModels(payload, findFn, userId, payload.planbookId)
  let context = defaultPlanbookContext(payload, models)
  let newContext = assign({}, context, {
    senderName: `${payload.user.attributes.firstName} ${payload.user.attributes.lastName}`,
    senderEmail: payload.user.attributes.email,
    recipients: (Array.isArray(payload.email) ? payload.email : [payload.email]).map((email) => ({
      email: email,
      id: email,
    })),
    subject: payload.subject,
    message: payload.message,
    url: payload.url,
    perspective: payload.perspective,
  })

  let primaryObjectSpecifier
  if (payload.perspective === "standards-tracker") {
    primaryObjectSpecifier = "standards_tracker"
  } else {
    primaryObjectSpecifier = payload.perspective
  }

  let newEvent: PlanbookShare = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    primary_object_type: "planbook",
    primary_possessive: "owns",
    primary_object_id_and_ancestor_ids: [payload.planbookId],
    primary_verb: "share",
    primary_object_specifier: primaryObjectSpecifier,
    adverbial_verb: "emailing",
    adverbial_object_type: "link",
    adverbial_object_specifier: null,
  }

  return createNarrative({
    title: `Emailed link from the ${payload.perspective} view.`,
    titleForAnalytics: "Planbook - Email Link",
    description: `Emailing link to the ${payload.perspective} view.`,
    context: newContext,
    newAnalyticsEvents: [newEvent],
  })
}
